import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Alert,
  CircularProgress,
  Pagination,
  Chip,
  TextField,
  Button,
  Rating,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Tooltip,
  Snackbar,
} from '@mui/material'
import {
  StarBorder as StarBorderIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Delete as DeleteIcon,
  Sort as SortIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Add as AddIcon,
  CloudUpload as SubmitIcon,
  LocalFireDepartment as SmokeIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material'
import {
  useSearchDatabaseQuery,
  useGetSavedSearchesQuery,
  useCreateSavedSearchMutation,
  useDeleteSavedSearchMutation,
  DatabaseSearchParams,
  DatabaseSearchResult,
} from '@store/api/databaseApi'
import {
  useAddToWishlistMutation,
  useAddToCollectionMutation,
  useGetCigarStatusQuery,
} from '@store/api/cigarDetailApi'
import { Cigar, useLazySearchCigarsQuery } from '@store/api/cigarApi'
import { useAppSelector } from '@store/hooks'
import PageHeader, { type PageHeaderOverflowItem } from '@components/layout/PageHeader'
import PageContent from '@components/layout/PageContent'
import { TOUCH_TARGET_MIN_PX } from '@components/layout/layoutConstants'
import { FindFiltersPanel, FindFilterValues } from '../features/find/components/FindFiltersPanel'
import { FIND_SEARCH_DEBOUNCE_MS } from '../features/find/constants/debounce'
import SessionForm from '@components/sessions/SessionForm'
import { trackFeature } from '@utils/trackFeature'

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'newest', label: 'Newest' },
  { value: 'name', label: 'Name (A-Z)' },
]

const EMPTY_FIND_FILTERS: FindFilterValues = {
  strength: '',
  wrapperType: '',
  origin: '',
  vitolaId: '',
  minPrice: '',
  maxPrice: '',
}

function parseOptionalPrice(value: string): number | undefined {
  if (!value.trim()) return undefined
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const DatabaseSearch: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const navigate = useNavigate()

  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showSavedSearches, setShowSavedSearches] = useState(false)
  const [filters, setFilters] = useState<FindFilterValues>(EMPTY_FIND_FILTERS)
  const [sortBy, setSortBy] = useState<'relevance' | 'rating' | 'newest' | 'name'>('relevance')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveSearchName, setSaveSearchName] = useState('')
  const [smokingCigar, setSmokingCigar] = useState<Cigar | null>(null)
  const [showSessionForm, setShowSessionForm] = useState(false)

  const skipInitialSearchTrack = useRef(true)
  const skipInitialFilterTrack = useRef(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput.trim())
      setPage(1)
    }, FIND_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const minPrice = parseOptionalPrice(filters.minPrice)
  const maxPrice = parseOptionalPrice(filters.maxPrice)

  const searchParams: DatabaseSearchParams = {
    page,
    limit: 12,
    sortBy,
    sortOrder,
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(filters.strength && { strength: filters.strength }),
    ...(filters.wrapperType && { wrapperType: filters.wrapperType }),
    ...(filters.origin && { origin: filters.origin }),
    ...(filters.vitolaId && { vitolaId: filters.vitolaId }),
    ...(minPrice !== undefined && { minPrice }),
    ...(maxPrice !== undefined && { maxPrice }),
  }

  const { data: searchData, isLoading, error } = useSearchDatabaseQuery(searchParams)
  const { data: savedSearches } = useGetSavedSearchesQuery(undefined, {
    skip: !isAuthenticated,
  })
  const [createSavedSearch, { isLoading: isSaving }] = useCreateSavedSearchMutation()
  const [deleteSavedSearch] = useDeleteSavedSearchMutation()

  const activeFilterCount = [
    filters.strength,
    filters.wrapperType,
    filters.origin,
    filters.vitolaId,
    filters.minPrice,
    filters.maxPrice,
  ].filter(Boolean).length

  const filterTrackSignature = useMemo(
    () =>
      JSON.stringify({
        strength: filters.strength,
        wrapperType: filters.wrapperType,
        origin: filters.origin,
        vitolaId: filters.vitolaId,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
      }),
    [filters]
  )

  useEffect(() => {
    if (skipInitialSearchTrack.current) {
      skipInitialSearchTrack.current = false
      return
    }
    // Input is already debounced into debouncedSearch; do not wait another 500ms.
    if (debouncedSearch) {
      trackFeature('search_used', {
        has_query: true,
        surface: 'find',
      })
    }
  }, [debouncedSearch])

  useEffect(() => {
    if (skipInitialFilterTrack.current) {
      skipInitialFilterTrack.current = false
      return
    }
    const timer = window.setTimeout(() => {
      if (activeFilterCount === 0) {
        return
      }
      const keys = [
        filters.strength && 'strength',
        filters.wrapperType && 'wrapperType',
        filters.origin && 'origin',
        filters.vitolaId && 'vitolaId',
        filters.minPrice && 'minPrice',
        filters.maxPrice && 'maxPrice',
      ]
        .filter(Boolean)
        .join(',')
      trackFeature('filter_used', {
        count_bucket: activeFilterCount,
        filter_key: keys,
        surface: 'find',
      })
    }, FIND_SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [filterTrackSignature, activeFilterCount, filters])

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value)
  }

  const handleFilterChange = <K extends keyof FindFilterValues>(key: K, value: FindFilterValues[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setPage(1)
  }

  const handleClearFilters = useCallback(() => {
    setFilters(EMPTY_FIND_FILTERS)
    setSortBy('relevance')
    setSortOrder('desc')
    setPage(1)
  }, [])

  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) return
    try {
      await createSavedSearch({
        name: saveSearchName.trim(),
        query: debouncedSearch || undefined,
        filters: {
          ...(filters.strength && { strength: filters.strength }),
          ...(filters.wrapperType && { wrapperType: filters.wrapperType }),
          ...(filters.origin && { origin: filters.origin }),
        },
        sortBy,
      }).unwrap()
      setSaveDialogOpen(false)
      setSaveSearchName('')
    } catch (err) {
      console.error('Failed to save search:', err)
    }
  }

  const handleLoadSavedSearch = (saved: {
    query?: string
    filters?: Record<string, unknown>
    sortBy?: string
  }) => {
    if (saved.query) {
      setSearchInput(saved.query)
    }
    if (saved.filters) {
      setFilters((prev) => ({
        ...prev,
        strength: (saved.filters?.strength as string) || '',
        wrapperType: (saved.filters?.wrapperType as string) || '',
        origin: (saved.filters?.origin as string) || '',
      }))
    }
    if (saved.sortBy) {
      setSortBy(saved.sortBy as typeof sortBy)
    }
    setShowSavedSearches(false)
  }

  const handleDeleteSavedSearch = async (id: string) => {
    try {
      await deleteSavedSearch(id).unwrap()
    } catch (err) {
      console.error('Failed to delete saved search:', err)
    }
  }

  const handleSubmitOverflow = useCallback(() => {
    trackFeature('overflow_action', { action: 'submit_cigar' })
    navigate('/app/database/submit')
  }, [navigate])

  const handleToggleSavedSearches = useCallback(() => {
    trackFeature('overflow_action', { action: showSavedSearches ? 'saved_searches_hide' : 'saved_searches_show' })
    setShowSavedSearches((prev) => !prev)
  }, [showSavedSearches])

  const handleOpenSaveSearch = useCallback(() => {
    trackFeature('overflow_action', { action: 'save_search_open' })
    setSaveDialogOpen(true)
  }, [])

  const handleStartSmoke = useCallback((cigar: Cigar | null) => {
    trackFeature('cta_smoke_start', { source: 'find' })
    setSmokingCigar(cigar)
    setShowSessionForm(true)
  }, [])

  const overflowItems = useMemo(() => {
    const items: PageHeaderOverflowItem[] = [
      {
        id: 'submit',
        label: 'Submit new cigar',
        icon: <SubmitIcon fontSize="small" />,
        onClick: handleSubmitOverflow,
        disabled: !isAuthenticated,
      },
    ]
    if (isAuthenticated) {
      items.push(
        {
          id: 'saved',
          label: showSavedSearches ? 'Hide saved searches' : 'Saved searches',
          icon: <BookmarkBorderIcon fontSize="small" />,
          onClick: handleToggleSavedSearches,
        },
        {
          id: 'save',
          label: 'Save current search',
          icon: <BookmarkIcon fontSize="small" />,
          onClick: handleOpenSaveSearch,
          disabled: !debouncedSearch && activeFilterCount === 0,
        }
      )
    }
    return items
  }, [
    isAuthenticated,
    showSavedSearches,
    debouncedSearch,
    activeFilterCount,
    handleSubmitOverflow,
    handleToggleSavedSearches,
    handleOpenSaveSearch,
  ])

  return (
    <PageContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
      <PageHeader title="Find" overflowItems={overflowItems} overflowMenuLabel="Find actions" />

      <FindFiltersPanel
        searchInput={searchInput}
        onSearchChange={handleSearchInputChange}
        onSearchClear={() => handleSearchInputChange('')}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
      />

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          maxWidth: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="find-sort-toolbar"
      >
        <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 140px', maxWidth: '100%' }}>
          <InputLabel id="find-sort-label">Sort By</InputLabel>
          <Select
            labelId="find-sort-label"
            value={sortBy}
            label="Sort By"
            onChange={(e) => {
              setSortBy(e.target.value as typeof sortBy)
              setPage(1)
            }}
            startAdornment={<SortIcon sx={{ mr: 0.5, fontSize: 18 }} />}
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          size="small"
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          sx={{ minHeight: TOUCH_TARGET_MIN_PX, flexShrink: 0 }}
        >
          {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        </Button>
      </Box>

      <Collapse in={showSavedSearches && isAuthenticated}>
        <Card sx={{ mb: 3, p: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Saved Searches
          </Typography>
          {savedSearches && savedSearches.length > 0 ? (
            <Stack spacing={1}>
              {savedSearches.map((saved) => (
                <Stack key={saved.id} direction="row" alignItems="center" spacing={1}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleLoadSavedSearch(saved)}
                    sx={{ textTransform: 'none', justifyContent: 'flex-start', flexGrow: 1 }}
                  >
                    {saved.name}
                    {saved.query && <Chip label={saved.query} size="small" sx={{ ml: 1 }} />}
                  </Button>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteSavedSearch(saved.id)}
                    aria-label={`Delete saved search ${saved.name}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No saved searches yet.
            </Typography>
          )}
        </Card>
      </Collapse>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load search results. Please try again.
        </Alert>
      )}

      {!isLoading && searchData && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchData.pagination.totalCount} result{searchData.pagination.totalCount !== 1 ? 's' : ''} found
          </Typography>

          <Grid container spacing={2}>
            {searchData.results.map((result) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={result.id}>
                <CigarLineCard
                  result={result}
                  isAuthenticated={isAuthenticated}
                  onSnackbar={setSnackbarMessage}
                  onStartSmoke={handleStartSmoke}
                />
              </Grid>
            ))}
          </Grid>

          {searchData.results.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }} data-testid="find-empty-state">
              <Typography variant="h6" color="text.secondary">
                No cigars found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Try adjusting your search or filters.
              </Typography>
              {isAuthenticated && (
                <Button
                  variant="contained"
                  startIcon={<SubmitIcon />}
                  onClick={() => {
                    trackFeature('empty_state_cta', { action: 'submit_cigar', surface: 'find' })
                    navigate('/app/database/submit')
                  }}
                  sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                >
                  Submit a cigar to the catalog
                </Button>
              )}
            </Box>
          )}

          {searchData.pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={searchData.pagination.totalPages}
                page={page}
                onChange={(_e, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save Search</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Search Name"
            value={saveSearchName}
            onChange={(e) => setSaveSearchName(e.target.value)}
            placeholder="e.g. Full Body Nicaraguan"
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {debouncedSearch && `Query: "${debouncedSearch}"`}
            {activeFilterCount > 0 && ` | ${activeFilterCount} filter(s) active`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveSearch}
            variant="contained"
            disabled={!saveSearchName.trim() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <SessionForm
        open={showSessionForm}
        onClose={() => {
          setShowSessionForm(false)
          setSmokingCigar(null)
        }}
        cigar={smokingCigar}
      />

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </PageContent>
  )
}

interface CigarLineCardProps {
  result: DatabaseSearchResult
  isAuthenticated: boolean
  onSnackbar: (message: string) => void
  onStartSmoke: (cigar: Cigar | null) => void
}

const CigarLineCard: React.FC<CigarLineCardProps> = ({
  result,
  isAuthenticated,
  onSnackbar,
  onStartSmoke,
}) => {
  const { data: status } = useGetCigarStatusQuery(result.id, { skip: !isAuthenticated })
  const [addToWishlist, { isLoading: isAddingWishlist }] = useAddToWishlistMutation()
  const [addToCollection, { isLoading: isAddingCollection }] = useAddToCollectionMutation()
  const [triggerSearchCigars] = useLazySearchCigarsQuery()
  const [justAdded, setJustAdded] = useState(false)
  const [isResolvingSmoke, setIsResolvingSmoke] = useState(false)

  const inCollection = status?.inCollection || justAdded

  const handleAddToWishlist = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await addToWishlist({ cigarId: result.id }).unwrap()
      onSnackbar(`${result.name} added to wishlist`)
    } catch (err: unknown) {
      const statusCode = (err as { status?: number })?.status
      if (statusCode === 409) {
        onSnackbar('Already in wishlist')
      } else {
        onSnackbar('Failed to add to wishlist')
      }
    }
  }

  const handleAddToCollection = async () => {
    try {
      await addToCollection({ cigarId: result.id }).unwrap()
      trackFeature('cta_cigar_add', { source: 'find', outcome: 'saved' })
      setJustAdded(true)
      onSnackbar(`${result.name} added to your collection`)
    } catch (err: unknown) {
      const statusCode = (err as { status?: number })?.status
      if (statusCode === 409) {
        trackFeature('cta_cigar_add', { source: 'find', outcome: 'saved' })
        setJustAdded(true)
        onSnackbar('Already in your collection')
      } else {
        onSnackbar('Failed to add to collection')
      }
    }
  }

  const handleSmoke = async () => {
    setIsResolvingSmoke(true)
    let cigar: Cigar | null = null
    try {
      const available = await triggerSearchCigars(
        {
          cigarLineId: result.id,
          status: 'available',
          limit: 1,
          page: 1,
        },
        false,
      ).unwrap()
      cigar = available.cigars[0] ?? null
      if (!cigar) {
        const anyStatus = await triggerSearchCigars(
          {
            cigarLineId: result.id,
            limit: 1,
            page: 1,
          },
          false,
        ).unwrap()
        cigar = anyStatus.cigars[0] ?? null
      }
    } catch {
      cigar = null
    } finally {
      setIsResolvingSmoke(false)
    }
    if (!cigar) {
      onSnackbar('Select this cigar from your humidors to start smoking')
    }
    onStartSmoke(cigar)
  }

  return (
    <Card
      data-testid="find-result-card"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            width: '100%',
            height: 120,
            backgroundColor: 'grey.200',
            borderRadius: 1,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: result.brand.imageUrl ? `url(${result.brand.imageUrl})` : 'none',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          {!result.brand.imageUrl && (
            <Typography variant="body2" color="text.secondary">
              No Image
            </Typography>
          )}
        </Box>

        <Typography variant="subtitle2" color="text.secondary" gutterBottom noWrap>
          {result.brand.name}
          {result.brand.country && ` (${result.brand.country})`}
        </Typography>
        <Typography variant="h6" component="h3" gutterBottom sx={{ wordBreak: 'break-word' }}>
          {result.name}
        </Typography>

        {result.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {result.description}
          </Typography>
        )}

        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {result.vitola && <Chip label={result.vitola.name} size="small" variant="outlined" />}
          {result.strengthLevel && <Chip label={result.strengthLevel} size="small" variant="outlined" />}
          {result.wrapperType && <Chip label={result.wrapperType} size="small" variant="outlined" />}
          {result.averagePrice != null && (
            <Chip label={`$${result.averagePrice.toFixed(2)}`} size="small" variant="outlined" />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          {result.averageRating !== null ? (
            <>
              <Rating
                value={result.averageRating}
                precision={0.1}
                readOnly
                size="small"
                emptyIcon={<StarBorderIcon fontSize="inherit" />}
              />
              <Typography variant="body2" color="text.secondary">
                ({result.totalReviews})
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No ratings yet
            </Typography>
          )}
        </Stack>
      </CardContent>

      {isAuthenticated && (
        <CardActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 2, pb: 2, pt: 0, gap: 1 }}>
          {inCollection ? (
            <Stack spacing={1} width="100%">
              <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="center">
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2" color="success.main">
                  In your collection
                </Typography>
              </Stack>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SmokeIcon />}
                onClick={handleSmoke}
                disabled={isResolvingSmoke}
                fullWidth
                sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
                data-testid="find-smoke-cta"
              >
                {isResolvingSmoke ? 'Opening…' : 'Smoke'}
              </Button>
            </Stack>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddToCollection}
              disabled={isAddingCollection}
              fullWidth
              sx={{ minHeight: TOUCH_TARGET_MIN_PX }}
              data-testid="find-add-to-collection"
            >
              {isAddingCollection ? 'Adding…' : 'Add to collection'}
            </Button>
          )}

          <Stack direction="row" justifyContent="flex-end" width="100%">
            <Tooltip title={status?.inWishlist ? 'In your wishlist' : 'Add to wishlist'}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleAddToWishlist}
                  disabled={isAddingWishlist || status?.inWishlist}
                  color={status?.inWishlist ? 'error' : 'default'}
                  aria-label="Add to wishlist"
                  sx={{ minWidth: TOUCH_TARGET_MIN_PX, minHeight: TOUCH_TARGET_MIN_PX }}
                >
                  {status?.inWishlist ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </CardActions>
      )}
    </Card>
  )
}

export default DatabaseSearch
